import React, { useState, useEffect } from 'react';
import { jobsAPI } from '../../utils/api';
import toastr from 'toastr';
import './InterviewFeedbackDrawer.css';

function InterviewFeedbackDrawer({ interview, isOpen, onClose, onSubmitted }) {
  const [formData, setFormData] = useState({
    feedback: '',
    rating: 0,
    recommendation: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Initialize form with existing feedback if available
  useEffect(() => {
    if (interview) {
      setFormData({
        feedback: interview.feedback || '',
        rating: interview.rating || 0,
        recommendation: interview.recommendation || ''
      });
    }
  }, [interview]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    // Validation
    if (!formData.feedback || formData.feedback.trim().length < 20) {
      setError('Feedback must be at least 20 characters long');
      return;
    }

    if (!formData.rating || formData.rating < 1 || formData.rating > 5) {
      setError('Please provide a rating between 1 and 5');
      return;
    }

    if (!formData.recommendation) {
      setError('Please select a recommendation');
      return;
    }

    try {
      setSubmitting(true);
      await jobsAPI.submitInterviewFeedback(interview._id, {
        feedback: formData.feedback.trim(),
        rating: formData.rating,
        recommendation: formData.recommendation
      });
      toastr.success('Feedback submitted successfully!');
      if (onSubmitted) {
        onSubmitted();
      }
      onClose();
    } catch (error) {
      console.error('Failed to submit feedback:', error);
      setError(error.response?.data?.error || 'Failed to submit feedback');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRatingClick = (rating) => {
    setFormData(prev => ({ ...prev, rating }));
  };

  const getRecommendationLabel = (value) => { // eslint-disable-line no-unused-vars
    const labels = {
      'strong_yes': 'Strong Yes - Highly Recommended',
      'yes': 'Yes - Recommended',
      'maybe': 'Maybe - Neutral',
      'no': 'No - Not Recommended',
      'strong_no': 'Strong No - Not Recommended'
    };
    return labels[value] || value;
  };

  if (!isOpen || !interview) return null;

  return (
    <div className="feedback-drawer-overlay" onClick={onClose}>
      <div className="feedback-drawer" onClick={(e) => e.stopPropagation()}>
        <div className="drawer-header">
          <h2>Submit Interview Feedback</h2>
          <button className="drawer-close" onClick={onClose}>×</button>
        </div>

        <div className="drawer-body">
          {/* Interview Summary */}
          <div className="interview-summary">
            <h3>Interview Details</h3>
            <div className="summary-grid">
              <div className="summary-item">
                <span className="label">Candidate:</span>
                <span className="value">{interview.candidate?.name || 'N/A'}</span>
              </div>
              <div className="summary-item">
                <span className="label">Position:</span>
                <span className="value">{interview.job?.title || 'N/A'}</span>
              </div>
              <div className="summary-item">
                <span className="label">Date:</span>
                <span className="value">
                  {interview.scheduledDate ? new Date(interview.scheduledDate).toLocaleDateString() : 'N/A'}
                </span>
              </div>
              <div className="summary-item">
                <span className="label">Status:</span>
                <span className="value">{interview.status || 'N/A'}</span>
              </div>
            </div>
          </div>

          {error && (
            <div className="error-message">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="feedback-form">
            {/* Rating */}
            <div className="form-section">
              <label className="form-label">
                Overall Rating <span className="required">*</span>
                {formData.rating > 0 && (
                  <span className="rating-value">{formData.rating}/5</span>
                )}
              </label>
              <div className="rating-stars">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    type="button"
                    className={`star-btn ${formData.rating >= star ? 'filled' : ''}`}
                    onClick={() => handleRatingClick(star)}
                    onMouseEnter={(e) => {
                      if (!submitting) {
                        e.currentTarget.classList.add('hover');
                      }
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.classList.remove('hover');
                    }}
                  >
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                    </svg>
                  </button>
                ))}
              </div>
              <p className="field-description">Click on a star to rate the candidate (1 = Poor, 5 = Excellent)</p>
            </div>

            {/* Recommendation */}
            <div className="form-section">
              <label className="form-label">
                Recommendation <span className="required">*</span>
              </label>
              <select
                className="form-select"
                value={formData.recommendation}
                onChange={(e) => setFormData(prev => ({ ...prev, recommendation: e.target.value }))}
                required
              >
                <option value="">Select a recommendation</option>
                <option value="strong_yes">Strong Yes - Highly Recommended</option>
                <option value="yes">Yes - Recommended</option>
                <option value="maybe">Maybe - Neutral</option>
                <option value="no">No - Not Recommended</option>
                <option value="strong_no">Strong No - Not Recommended</option>
              </select>
              <p className="field-description">Your hiring recommendation for this candidate</p>
            </div>

            {/* Feedback Notes */}
            <div className="form-section">
              <label className="form-label">
                Feedback <span className="required">*</span>
                <span className="char-count">
                  {formData.feedback.length} / 20 minimum
                </span>
              </label>
              <textarea
                className="form-textarea"
                rows="8"
                value={formData.feedback}
                onChange={(e) => setFormData(prev => ({ ...prev, feedback: e.target.value }))}
                placeholder="Provide detailed feedback about the candidate's performance, skills, communication, strengths, areas for improvement, and any other relevant observations..."
                required
                minLength={20}
              />
              <p className="field-description">Please provide detailed feedback (minimum 20 characters). Include observations about technical skills, communication, problem-solving, and overall fit.</p>
            </div>

            {/* Form Actions */}
            <div className="form-actions">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={onClose}
                disabled={submitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={
                  submitting ||
                  !formData.feedback.trim() ||
                  formData.feedback.trim().length < 20 ||
                  !formData.rating ||
                  !formData.recommendation
                }
              >
                {submitting ? 'Submitting...' : 'Submit Feedback'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default InterviewFeedbackDrawer;

